import { useState, useEffect } from 'react';
import {
  Title,
  Group,
  Button,
  Stack,
  Paper,
  Text,
  Card,
  Grid,
  Badge,
  Alert,
  Table,
  ThemeIcon,
  Modal,
  TextInput,
  Divider,
  Code,
  Collapse,
  ActionIcon,
  Select,
  Loader,
  Center,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconDeviceDesktop,
  IconArrowRight,
  IconShieldCheck,
  IconBuildingSkyscraper,
  IconPhoto,
  IconRefresh,
  IconExclamationCircle,
} from '@tabler/icons-react';

import { useCreatePreview, usePreview, useApplyChanges } from '@/hooks/usePreview';
import { ErrorState } from '@/components/common/ErrorState';
import type { PreviewResponse, DeviceChange, ApplyRequest } from '@/types';

export function PreviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] =
    useDisclosure();
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [expandedDevices, setExpandedDevices] = useState<string[]>([]);
  const [maintenanceWindow, setMaintenanceWindow] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Get device IDs from navigation state
  const selectedDeviceIds = (location.state?.deviceIds as string[]) || [];

  // Get existing preview ID if navigating from saved preview
  const existingPreviewId = (location.state?.previewId as string) || null;

  // Mutations and queries
  const createPreviewMutation = useCreatePreview();
  const applyChangesMutation = useApplyChanges();

  // Fetch existing preview if we have an ID
  const existingPreviewQuery = usePreview(existingPreviewId || previewId);

  // Create preview when page loads with device IDs
  useEffect(() => {
    if (selectedDeviceIds.length > 0 && !existingPreviewId && !previewId) {
      createPreviewMutation.mutate(
        { deviceIds: selectedDeviceIds },
        {
          onSuccess: (data) => {
            setPreviewId(data.previewId);
            notifications.show({
              title: 'Preview generated',
              message: `Preview created for ${data.summary.totalDevices} devices`,
              color: 'blue',
            });
          },
          onError: (error) => {
            notifications.show({
              title: 'Preview failed',
              message: error instanceof Error ? error.message : 'Failed to generate preview',
              color: 'red',
            });
          },
        }
      );
    }
  }, [selectedDeviceIds, existingPreviewId, previewId]);

  // Use existing preview data or newly created preview
  const previewData: PreviewResponse | null =
    existingPreviewQuery.data || createPreviewMutation.data || null;

  const isLoading = createPreviewMutation.isPending || existingPreviewQuery.isLoading;
  const isError = createPreviewMutation.isError || existingPreviewQuery.isError;
  const error = createPreviewMutation.error || existingPreviewQuery.error;

  const toggleDevice = (deviceId: string) => {
    setExpandedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const expectedConfirmation = previewData
    ? `APPLY TO ${previewData.summary.affectedDevices} DEVICES`
    : '';

  const handleApply = () => {
    if (!previewData || typedConfirmation !== expectedConfirmation) return;

    const request: ApplyRequest = {
      previewId: previewData.previewId,
      typedConfirmation,
      maintenanceWindow: maintenanceWindow || undefined,
    };

    applyChangesMutation.mutate(request, {
      onSuccess: (result) => {
        notifications.show({
          title: 'Changes applied',
          message: `Successfully queued changes for ${result.summary.succeeded} devices`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        closeConfirmModal();
        navigate('/tasks', { state: { taskId: result.taskId } });
      },
      onError: (error) => {
        notifications.show({
          title: 'Apply failed',
          message: error instanceof Error ? error.message : 'Failed to apply changes',
          color: 'red',
        });
      },
    });
  };

  const handleRetry = () => {
    if (selectedDeviceIds.length > 0) {
      createPreviewMutation.mutate({ deviceIds: selectedDeviceIds });
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'green';
      case 'medium':
        return 'yellow';
      case 'high':
        return 'orange';
      case 'critical':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Preview Changes</Title>
            <Text c="dimmed" size="sm">
              Generating preview...
            </Text>
          </div>
        </Group>

        <Center py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">
              Evaluating rules for {selectedDeviceIds.length} devices...
            </Text>
          </Stack>
        </Center>

        <Grid>
          {[1, 2, 3, 4].map((i) => (
            <Grid.Col key={i} span={{ base: 12, md: 3 }}>
              <Skeleton height={120} radius="md" />
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    );
  }

  // Error state
  if (isError) {
    return (
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Preview Changes</Title>
            <Text c="dimmed" size="sm">
              Failed to generate preview
            </Text>
          </div>
        </Group>

        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to generate preview'}
          onRetry={handleRetry}
        />
      </Stack>
    );
  }

  // No devices selected
  if (!previewData && selectedDeviceIds.length === 0 && !existingPreviewId) {
    return (
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Preview Changes</Title>
            <Text c="dimmed" size="sm">
              No devices selected
            </Text>
          </div>
        </Group>

        <Alert
          icon={<IconExclamationCircle size={16} />}
          title="No devices to preview"
          color="yellow"
        >
          Please select devices from the Devices page to preview changes.
        </Alert>

        <Button
          onClick={() => navigate('/devices')}
          leftSection={<IconDeviceDesktop size={16} />}
        >
          Go to Devices
        </Button>
      </Stack>
    );
  }

  // Preview not ready yet
  if (!previewData) {
    return null;
  }

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Preview Changes</Title>
            <Text c="dimmed" size="sm">
              Review proposed changes before applying
            </Text>
          </div>

          <Group>
            <Select
              placeholder="Maintenance window"
              data={[
                { value: 'tonight', label: 'Tonight (10 PM - 6 AM)' },
                { value: 'weekend', label: 'This weekend' },
                { value: 'immediate', label: 'Immediate (override)' },
              ]}
              value={maintenanceWindow}
              onChange={(v) => setMaintenanceWindow(v || '')}
              w={200}
            />
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRetry}
              loading={createPreviewMutation.isPending}
            >
              Regenerate
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={openConfirmModal}
              disabled={previewData.summary.affectedDevices === 0}
            >
              Apply Changes
            </Button>
          </Group>
        </Group>

        {/* Preview expiration warning */}
        {previewData.expiresAt && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="blue"
            variant="light"
          >
            This preview expires at {new Date(previewData.expiresAt).toLocaleString()}.
            Regenerate if needed.
          </Alert>
        )}

        {/* Warnings */}
        {previewData.warnings.length > 0 && (
          <Stack gap="xs">
            {previewData.warnings.map((warning, index) => (
              <Alert
                key={index}
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                variant="light"
              >
                {warning.message}
              </Alert>
            ))}
          </Stack>
        )}

        {/* Summary cards */}
        <Grid>
          {/* Affected devices */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Affected Devices
                  </Text>
                  <Text size="xl" fw={700}>
                    {previewData.summary.affectedDevices}
                  </Text>
                  <Text size="xs" c="dimmed">
                    of {previewData.summary.totalDevices} selected
                  </Text>
                </div>
                <ThemeIcon variant="light" size="xl" radius="md">
                  <IconDeviceDesktop size={24} />
                </ThemeIcon>
              </Group>
            </Card>
          </Grid.Col>

          {/* Risk score */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Risk Level
                  </Text>
                  <Group gap="xs" align="baseline">
                    <Text size="xl" fw={700}>
                      {previewData.riskSummary.averageScore}
                    </Text>
                    <Badge color={getRiskColor(previewData.riskSummary.level)} size="sm">
                      {previewData.riskSummary.level.toUpperCase()}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {previewData.riskSummary.highRiskCount} high-risk devices
                  </Text>
                </div>
                <ThemeIcon
                  variant="light"
                  size="xl"
                  radius="md"
                  color={getRiskColor(previewData.riskSummary.level)}
                >
                  <IconShieldCheck size={24} />
                </ThemeIcon>
              </Group>
            </Card>
          </Grid.Col>

          {/* Reimages */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Reimages
                  </Text>
                  <Text size="xl" fw={700} c="orange">
                    {previewData.summary.reimages}
                  </Text>
                  <Text size="xs" c="dimmed">
                    devices will be reimaged
                  </Text>
                </div>
                <ThemeIcon variant="light" size="xl" radius="md" color="orange">
                  <IconPhoto size={24} />
                </ThemeIcon>
              </Group>
            </Card>
          </Grid.Col>

          {/* Blast radius */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Card withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Blast Radius
                  </Text>
                  <Group gap="xs" align="baseline">
                    <Text size="xl" fw={700}>
                      {previewData.blastRadius.percentage.toFixed(1)}%
                    </Text>
                    {previewData.blastRadius.exceeds ? (
                      <Badge color="red" size="sm">
                        EXCEEDS
                      </Badge>
                    ) : (
                      <Badge color="green" size="sm">
                        OK
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    threshold: {previewData.blastRadius.threshold}%
                  </Text>
                </div>
                <ThemeIcon variant="light" size="xl" radius="md">
                  <IconBuildingSkyscraper size={24} />
                </ThemeIcon>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Breakdown by site and image */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder>
              <Text fw={500} mb="md">
                By Site
              </Text>
              <Stack gap="xs">
                {previewData.bySite.map((site) => (
                  <Group key={site.name} justify="space-between">
                    <Text size="sm">{site.name}</Text>
                    <Group gap="xs">
                      <Badge variant="light">{site.affected} devices</Badge>
                      {site.reimages > 0 && (
                        <Badge color="orange" variant="light">
                          {site.reimages} reimages
                        </Badge>
                      )}
                    </Group>
                  </Group>
                ))}
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder>
              <Text fw={500} mb="md">
                By Target Image
              </Text>
              <Stack gap="xs">
                {previewData.byImage.map((image) => (
                  <Group key={image.name} justify="space-between">
                    <Text size="sm">{image.name}</Text>
                    <Badge variant="light">{image.count} devices</Badge>
                  </Group>
                ))}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Device changes */}
        <Paper withBorder>
          <Group justify="space-between" p="md" pb={0}>
            <Text fw={500}>Device Changes</Text>
            <Text size="sm" c="dimmed">
              {previewData.deviceChanges.length} devices with changes
            </Text>
          </Group>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}></Table.Th>
                <Table.Th>Device</Table.Th>
                <Table.Th>Site</Table.Th>
                <Table.Th>Changes</Table.Th>
                <Table.Th>Risk</Table.Th>
                <Table.Th>Matched Rule</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {previewData.deviceChanges.map((device) => (
                <DeviceChangeRow
                  key={device.deviceId}
                  device={device}
                  isExpanded={expandedDevices.includes(device.deviceId)}
                  onToggle={() => toggleDevice(device.deviceId)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>

      {/* Confirmation modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="orange" />
            <Text fw={600}>Confirm Apply Changes</Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
            This action will apply changes to{' '}
            <strong>{previewData.summary.affectedDevices} devices</strong>, including{' '}
            <strong>{previewData.summary.reimages} reimages</strong>.
          </Alert>

          <Text size="sm">
            To confirm, type:{' '}
            <Code>{expectedConfirmation}</Code>
          </Text>

          <TextInput
            placeholder="Type confirmation..."
            value={typedConfirmation}
            onChange={(e) => setTypedConfirmation(e.currentTarget.value)}
            error={
              typedConfirmation.length > 0 &&
              typedConfirmation !== expectedConfirmation
                ? 'Confirmation does not match'
                : undefined
            }
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={closeConfirmModal}>
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleApply}
              disabled={typedConfirmation !== expectedConfirmation}
              loading={applyChangesMutation.isPending}
            >
              Apply Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// Separate component for device change row to avoid key issues with fragments
function DeviceChangeRow({
  device,
  isExpanded,
  onToggle,
}: {
  device: DeviceChange;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <Table.Tr
        style={{ cursor: 'pointer' }}
        onClick={onToggle}
        bg={device.isHighRisk ? 'var(--mantine-color-red-light)' : undefined}
      >
        <Table.Td>
          <ActionIcon variant="subtle" size="sm">
            {isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <Text fw={500}>{device.hostname}</Text>
            {device.isHighRisk && (
              <Badge color="red" size="xs">
                HIGH RISK
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{device.site}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap={4}>
            {device.changes.map((change, i) => (
              <Badge
                key={i}
                size="xs"
                variant="light"
                color={change.changeType === 'create' ? 'green' : 'blue'}
              >
                {change.field}
              </Badge>
            ))}
          </Group>
        </Table.Td>
        <Table.Td>
          <Badge
            color={
              device.riskScore >= 80
                ? 'red'
                : device.riskScore >= 50
                ? 'orange'
                : device.riskScore >= 30
                ? 'yellow'
                : 'green'
            }
          >
            {device.riskScore}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Code>{device.matchedRule}</Code>
        </Table.Td>
      </Table.Tr>

      {/* Expanded details */}
      <Table.Tr>
        <Table.Td colSpan={6} p={0}>
          <Collapse in={isExpanded}>
            <Paper p="md" bg="var(--mantine-color-gray-light)">
              <Grid>
                {/* Changes diff */}
                <Grid.Col span={6}>
                  <Text size="sm" fw={500} mb="xs">
                    Changes
                  </Text>
                  <Stack gap="xs">
                    {device.changes.map((change, i) => (
                      <Group key={i} gap="xs">
                        <Text size="sm" c="dimmed" w={100}>
                          {change.field}:
                        </Text>
                        <Code color="red">{String(change.from) || 'null'}</Code>
                        <IconArrowRight size={14} />
                        <Code color="green">{String(change.to)}</Code>
                      </Group>
                    ))}
                  </Stack>
                </Grid.Col>

                {/* Rule explanation */}
                <Grid.Col span={6}>
                  <Text size="sm" fw={500} mb="xs">
                    Why this decision?
                  </Text>
                  {device.explanation ? (
                    <Card withBorder p="sm">
                      <Text size="sm" fw={500}>
                        {device.explanation.ruleName}
                      </Text>
                      <Text size="xs" c="dimmed" mb="xs">
                        Priority: {device.explanation.priority}
                      </Text>
                      <Divider my="xs" />
                      <Text size="xs" fw={500} mb="xs">
                        Matched conditions:
                      </Text>
                      <Stack gap={4}>
                        {device.explanation.conditions.map((cond, i) => (
                          <Group key={i} gap="xs">
                            {cond.matched ? (
                              <IconCheck size={12} color="green" />
                            ) : (
                              <IconX size={12} color="red" />
                            )}
                            <Text size="xs">
                              {cond.field} {cond.operator} "{String(cond.value)}"
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </Card>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No rule explanation available
                    </Text>
                  )}
                </Grid.Col>
              </Grid>
            </Paper>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}
